import React, { useState } from 'react';
import { RepoData } from './types';

interface RepoLeaderboardProps {
    repoData: RepoData[];
    currentTheme: any;
    sortBy: 'stars' | 'forks' | 'issues';
    sortOrder: 'asc' | 'desc';
    handleSortChange: (column: 'stars' | 'forks' | 'issues') => void;
    loading: boolean;
    error: string | null;
    repoSearchFilter: string;
    setRepoSearchFilter: React.Dispatch<React.SetStateAction<string>>;
}

const RepoLeaderboard: React.FC<RepoLeaderboardProps> = ({
    repoData,
    currentTheme,
    sortBy,
    sortOrder,
    handleSortChange,
    loading,
    error,
    repoSearchFilter,
    setRepoSearchFilter
}) => {
    // Sort the data based on current sort settings
    const [darkMode, setDarkMode] = useState<boolean>(document.body.classList.contains('dark-mode'));
    const sortedData = [...repoData].sort((a, b) => {
        let aVal = 0, bVal = 0;

        switch (sortBy) {
            case 'stars':
                aVal = a.num_stars;
                bVal = b.num_stars;
                break;
            case 'forks':
                aVal = a.num_forks;
                bVal = b.num_forks;
                break;
            case 'issues':
                aVal = a.num_issues;
                bVal = b.num_issues;
                break;
        }

        return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

    const getSortIndicator = (column: 'stars' | 'forks' | 'issues') => {
        if (sortBy !== column) return '';
        return sortOrder === 'desc' ? ' ↓' : ' ↑';
    };

    const getMetricValue = (repo: RepoData) => {
        switch (sortBy) {
            case 'stars':
                return repo.num_stars;
            case 'forks':
                return repo.num_forks;
            case 'issues':
                return repo.num_issues;
        }
    };

    const getMetricLabel = () => {
        switch (sortBy) {
            case 'stars':
                return 'stars';
            case 'forks':
                return 'forks';
            case 'issues':
                return 'issues';
        }
    };

    return (
        <div className="mt-6 md:mt-8 p-3 md:p-4 rounded-lg" style={{ backgroundColor: currentTheme.chartBg }}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                <h2 className="text-base md:text-lg font-semibold" style={{ color: currentTheme.primary }}>
                    Repository Leaderboard
                </h2>
            </div>

            {loading ? (
                <div className="py-4 text-center" style={{ color: currentTheme.text }}>
                    Loading repository data...
                </div>
            ) : error ? (
                <div className="py-4 text-center text-red-500">
                    {error}
                </div>
            ) : (
                <>
                    {/* Podium for Top 3 - Mobile version */}
                    <div className="flex md:hidden flex-col mb-6 space-y-4">
                        {sortedData.slice(0, 3).map((repo, index) => (
                            <div key={repo.repo_name} className="p-3 rounded-lg flex items-center" style={{ backgroundColor: currentTheme.controlsBg }}>
                                <div className="flex-grow">
                                    <div className="flex items-center">
                                        <div className="font-bold text-sm mr-2" style={{ color: currentTheme.primary }}>#{index + 1}</div>
                                        <a
                                            href={`https://github.com/${repo.repo_name}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: currentTheme.text }}
                                        >
                                            {repo.repo_name}
                                        </a>
                                    </div>
                                    <div className="text-sm" style={{ color: currentTheme.text }}>
                                        {getMetricValue(repo)} {getMetricLabel()}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Podium for Top 3 - Desktop version */}
                    {sortedData.length > 0 && (
                        <div className="hidden md:flex justify-center items-end mb-8 py-4">
                            {/* Second Place */}
                            {sortedData.length > 1 && (
                                <div className="text-center mx-4 w-28 flex flex-col items-center">
                                    <div
                                        className="relative h-24 w-20 flex items-center justify-center rounded-lg mb-2 overflow-hidden">
                                        <img
                                            src={currentTheme.leaderboard2}
                                            alt="Algorand Dashboard"
                                            className="absolute inset-0 h-full w-full object-contain"
                                        />
                                    </div>
                                    <a
                                        href={`https://github.com/${sortedData[1].repo_name}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-center truncate w-full"
                                        style={{ color: currentTheme.primary, fontWeight: 'bold' }}
                                        title={sortedData[1].repo_name}
                                    >
                                        {sortedData[1].repo_name.split('/').pop()}
                                    </a>
                                    <div style={{ color: currentTheme.text }}>{getMetricValue(sortedData[1])} {getMetricLabel()}</div>
                                </div>
                            )}

                            {/* First Place */}
                            <div className="text-center mx-4 -mb-4 w-32 flex flex-col items-center">
                                <div className="relative h-32 w-24 flex items-center justify-center rounded-lg mb-2 overflow-hidden">
                                    <img
                                        src={currentTheme.leaderboard1}
                                        alt="Algorand Dashboard"
                                        className="absolute inset-0 h-full w-full object-contain"
                                    />
                                </div>
                                <a
                                    href={`https://github.com/${sortedData[0].repo_name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-center truncate w-full"
                                    style={{ color: currentTheme.primary, fontWeight: 'bold', fontSize: '1.1rem' }}
                                    title={sortedData[0].repo_name}
                                >
                                    {sortedData[0].repo_name.split('/').pop()}
                                </a>
                                <div style={{ color: currentTheme.text, fontWeight: 'bold' }}>{getMetricValue(sortedData[0])} {getMetricLabel()}</div>
                            </div>

                            {/* Third Place */}
                            {sortedData.length > 2 && (
                                <div className="text-center mx-4 w-28 flex flex-col items-center">
                                    <div
                                        className="relative h-20 w-20 flex items-center justify-center rounded-lg mb-2 overflow-hidden">
                                        <img
                                            src={currentTheme.leaderboard3}
                                            alt="Algorand Dashboard"
                                            className="absolute inset-0 h-full w-full object-contain"
                                        />
                                    </div>
                                    <a
                                        href={`https://github.com/${sortedData[2].repo_name}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-center truncate w-full"
                                        style={{ color: currentTheme.primary, fontWeight: 'bold' }}
                                        title={sortedData[2].repo_name}
                                    >
                                        {sortedData[2].repo_name.split('/').pop()}
                                    </a>
                                    <div style={{ color: currentTheme.text }}>{getMetricValue(sortedData[2])} {getMetricLabel()}</div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Developer search filter */}
                    <div className="mb-4">
                        <div className="flex items-center">
                            <label
                                htmlFor="repoSearchFilter"
                                className="mr-2 text-xs md:text-sm font-medium"
                                style={{ color: currentTheme.primary }}
                            >
                                Search Repository:
                            </label>
                            <input
                                id="repoSearchFilter"
                                type="text"
                                value={repoSearchFilter}
                                onChange={(e) => setRepoSearchFilter(e.target.value)}
                                placeholder="Filter by repository name..."
                                className="px-3 py-1 text-xs md:text-sm rounded-md w-full max-w-xs"
                                style={{
                                    backgroundColor: currentTheme.controlsBg,
                                    color: currentTheme.text,
                                    border: `1px solid ${currentTheme.tableBorder}`
                                }}
                            />
                        </div>
                    </div>

                    {/* Scrollable List for Other Repositories */}
                    <h3 className="text-sm md:text-md font-semibold mb-2" style={{ color: currentTheme.primary }}>
                        Other Top Repositories
                    </h3>
                    <div className="overflow-x-auto" style={{ maxHeight: "250px", overflowY: "auto" }}>
                        <table className="w-full" style={{ color: currentTheme.text }}>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr style={{ backgroundColor: currentTheme.tableHeader }}>
                                    <th className="py-2 px-2 md:px-4 text-left border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                        Rank
                                    </th>
                                    <th className="py-2 px-2 md:px-4 text-left border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                        Repository
                                    </th>
                                    <th
                                        className="py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm cursor-pointer hover:opacity-80"
                                        style={{ borderColor: currentTheme.tableBorder }}
                                        onClick={() => handleSortChange('stars')}
                                    >
                                        Stars{getSortIndicator('stars')}
                                    </th>
                                    <th
                                        className="py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm cursor-pointer hover:opacity-80"
                                        style={{ borderColor: currentTheme.tableBorder }}
                                        onClick={() => handleSortChange('forks')}
                                    >
                                        Forks{getSortIndicator('forks')}
                                    </th>
                                    <th
                                        className="py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm cursor-pointer hover:opacity-80"
                                        style={{ borderColor: currentTheme.tableBorder }}
                                        onClick={() => handleSortChange('issues')}
                                    >
                                        Issues{getSortIndicator('issues')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.slice(3).map((entry, index) => (
                                    <tr
                                        key={entry.repo_name}
                                        style={{
                                            backgroundColor: index % 2 === 0 ?
                                                currentTheme.tableRowEven :
                                                currentTheme.tableRowOdd
                                        }}
                                    >
                                        <td className="py-1 md:py-2 px-2 md:px-4 border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                            {index + 4}
                                        </td>
                                        <td className="py-1 md:py-2 px-2 md:px-4 border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                            <a
                                                href={`https://github.com/${entry.repo_name}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: currentTheme.primary, textDecoration: "underline" }}
                                            >
                                                {entry.repo_name}
                                            </a>
                                        </td>
                                        <td className="py-1 md:py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                            {entry.num_stars}
                                        </td>
                                        <td className="py-1 md:py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                            {entry.num_forks}
                                        </td>
                                        <td className="py-1 md:py-2 px-2 md:px-4 text-right border-b text-xs md:text-sm" style={{ borderColor: currentTheme.tableBorder }}>
                                            {entry.num_issues}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            <div className="flex justify-end mt-4">
                <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: currentTheme.text }}>Powered by:</span>
                    <a href="https://opendevdata.org/" target="_blank" rel="noopener noreferrer">
                        <img
                            src={darkMode ? "/open_dev_data/3.Public Assets/logo-white.svg" : "/open_dev_data/3.Public Assets/logo.svg"}
                            alt="Open Dev Data"
                            className="h-6"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default RepoLeaderboard;
